import React from "react";
import PropTypes from "prop-types";
import { TextField, Chip, Autocomplete } from "@mui/material";
import { Controller } from "react-hook-form-v7";

const compactAutocompleteSx = {
  mt: 0,
  mb: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    py: 0.25,
    px: 0.75
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    fontSize: "0.8125rem"
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem"
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(12px, -7px) scale(0.75)"
  },
  "& .MuiChip-root": {
    height: 22,
    fontSize: "0.75rem",
    m: "2px"
  }
};

const MultiSelectChip = ({
  control,
  filter,
  data = [],
  name = "value",
  defaultValue = [],
  multiSelect = true,
  label,
  disabled,
  onChange,
  compact = false,
  sx
}) => {
  const autocompleteSx = compact
    ? { ...compactAutocompleteSx, ...sx }
    : { marginBottom: 8, marginTop: 4, ...sx };

  return (
    <>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Autocomplete
            multiple={multiSelect}
            size={compact ? "small" : "medium"}
            sx={autocompleteSx}
            options={data.filter((option) => {
              const selected = Array.isArray(field?.value)
                ? field.value
                : field?.value
                ? [field.value]
                : [];
              return !selected.some((item) => item.value === option.value);
            })}
            value={field?.value || []}
            disabled={disabled}
            onChange={(_, value) => {
              field.onChange(value);
              onChange && onChange(value);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
            getOptionLabel={(option) => option?.title || ""}
            getOptionKey={(option) => option.value}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.title}
                  size="small"
                  variant="outlined"
                  style={compact ? undefined : { margin: 5 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size={compact ? "small" : "medium"}
                disabled={disabled}
                sx={
                  compact
                    ? undefined
                    : {
                        "& .MuiInputBase-root": {
                          paddingLeft: 0,
                          paddingRight: 0
                        },
                        "& input": {
                          paddingLeft: 0,
                          paddingRight: 0,
                          fontSize: 14
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: 14
                        },
                        "& .MuiInputLabel-shrink": {
                          transform: "translate(20px, -6px) scale(0.75)"
                        }
                      }
                }
                label={label}
              />
            )}
          />
        )}
      />
    </>
  );
};

MultiSelectChip.propTypes = {
  control: PropTypes.shape({}),
  filter: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  name: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
  multiSelect: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  compact: PropTypes.bool,
  sx: PropTypes.shape({})
};

MultiSelectChip.defaultProps = {
  control: undefined,
  filter: undefined,
  data: [],
  name: "value",
  defaultValue: [],
  multiSelect: true,
  label: undefined,
  disabled: false,
  onChange: undefined,
  compact: false,
  sx: undefined
};

export default MultiSelectChip;
